import { useEffect, useRef, useState } from 'react'
import Tools from './Tools'
import Skills from './Skills'
import { normalizeSkillNames } from './skillUtils'
import { useDispatch, useSelector } from 'react-redux'
import { useAgentService } from './agentService'
import DeleteAgentModal from '../modals/DeleteAgentModal'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import { setAgentDetails, setInitialState } from '../../../../_redux/actions/agentActions'
import { Card, Spinner } from 'reactstrap'
import AgentBasicDetails from './AgentBasicDetails'
import AgentSummary from './AgentSummary'
import { AgentFormProvider, useAgentContext } from './AgentFormContext'
import DataSources from './DataSources'

const DataSourceDropdown = () => {
  const { setIsAddSourceModalOpen } = useAgentContext()

  return (
    <div className="ms-auto justify-content-end d-flex gap-2">
      <div
        className="border border-primary p-50 btn-sm d-flex align-items-center gap-50 cursor-pointer"
        onClick={() => setIsAddSourceModalOpen(true)}>
        <span className="text-primary">Add sources</span>
      </div>
    </div>
  )
}
const AgentWizard = () => {
  const [step, setStep] = useState<'details' | 'tools' | 'dataSources'>('details')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const navigate = useNavigate()
  const { agentId } = useParams<{ agentId?: string }>()
  const { getAgentDetails } = useAgentService()
  const dispatch = useDispatch()
  const { agent } = useSelector((state: any) => state.agentReducer)
  const [isValidationTriggered, setIsValidationTriggered] = useState(false)
  const agentNameValidationRef = useRef(undefined)
  const { mcpServers } = useSelector((state: any) => state.chatbotReducer)
  const { getAllMCPServers } = useAgentService()

  // Ensure mcpServers is loaded even if the Chatbot page (the only other place that fetches it)
  // was never visited - otherwise handleSubmitAgent silently drops all data sources on submit.
  useEffect(() => {
    if (!mcpServers || mcpServers.length === 0) {
      getAllMCPServers()
    }
  }, [])

  // Steps configuration array
  const stepsConfig = [
    {
      key: 'details',
      title: 'Agent setup',
      description: "Define the agent's identity and core behaviour."
    },
    {
      key: 'tools',
      title: 'Skills',
      description: 'Select skills that give the agent new capabilities.'
    },
    {
      key: 'dataSources',
      title: 'Data sources',
      description: 'Link the agent to data sources that power its analysis and insights.'
    }
  ]

  const getStepNumber = (currentStep: string) => {
    const stepIndex = stepsConfig.findIndex((step) => step.key === currentStep)
    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  const getCurrentStepConfig = (currentStep: string) => {
    return stepsConfig.find((step) => step.key === currentStep) || stepsConfig[0]
  }

  const onValidationCallback = () => {
    setIsValidationTriggered(true)
  }

  useEffect(() => {
    const fetchAgentData = async () => {
      setIsLoading(true)
      // Clear old agent data first
      dispatch(setInitialState())

      if (agentId) {
        try {
          const response = await getAgentDetails(agentId)
          const rawSkills = response?.skills ?? response?.agent?.skills
          const hydratedAgent = {
            ...response?.agent,
            skills: normalizeSkillNames(rawSkills)
          }
          dispatch(setAgentDetails(hydratedAgent))
          setAvatarFile(response?.agent?.avatarUrl || null)
        } catch (error) {
          console.error('Error fetching agent details:', error)
          toast.error('Failed to load agent details')
          navigate('/manage-agents')
        }
      }
      setIsLoading(false)
    }

    fetchAgentData()
  }, [agentId])

  return (
    <AgentFormProvider>
      {!isLoading && agent ? (
        <div className="mx-0 mt-2 max-h-[calc(100%-100px)] mb-0 flex justify-center">
          <Card className="theme-overlay-card max-w-[1500px] w-[80vw] flex-1 mb-0">
            <div className="flex gap-6">
              <div className="flex-1 min-w-0 p-2">
                {/* Step Indicator */}
                <div className="flex items-center gap-1 mb-1">
                  <div className="flex items-center justify-center w-16 h-16 bg-[var(--color-primary)] text-white rounded-lg">
                    <div className="flex flex-col items-center">
                      <span className="leading-tight">STEP</span>
                      <span className="flex items-end">
                        <span className="text-2xl font-extrabold leading-tight">
                          {getStepNumber(step)}
                        </span>
                        <span className="text-lg leading-tight">/{stepsConfig.length}</span>
                      </span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-0 text-dark">
                      {getCurrentStepConfig(step).title}
                    </h3>
                    <p className="text-sm theme-text-subheading mb-0 mt-50">
                      {getCurrentStepConfig(step).description}
                    </p>
                  </div>
                  {step === 'dataSources' && <DataSourceDropdown />}
                </div>
                {step === 'details' && (
                  <AgentBasicDetails
                    setStep={setStep}
                    setIsDeleteModalOpen={() => setIsDeleteModalOpen(!isDeleteModalOpen)}
                    avatarFile={avatarFile}
                    setAvatarFile={setAvatarFile}
                    isValidationTriggered={isValidationTriggered}
                    agentNameValidationRef={agentNameValidationRef}
                    onValidationCallback={onValidationCallback}
                  />
                )}
                {step === 'tools' && <Skills setStep={setStep} />}
                {step === 'dataSources' && (
                  <DataSources
                    setStep={setStep}
                    avatarFile={avatarFile}
                    isValidationTriggered={isValidationTriggered}
                    agentNameValidationRef={agentNameValidationRef}
                    onValidationCallback={onValidationCallback}
                  />
                )}
              </div>

              <div className="vertical-divider self-stretch" />

              <div className="w-[380px] p-2">
                <AgentSummary />
              </div>
            </div>
          </Card>

          {isDeleteModalOpen && (
            <DeleteAgentModal
              isOpen={isDeleteModalOpen}
              toggle={() => setIsDeleteModalOpen(!isDeleteModalOpen)}
              agent={agent}
            />
          )}
        </div>
      ) : (
        <div className="w-screen flex items-center justify-center h-screen">
          <Spinner color="primary" />
        </div>
      )}
    </AgentFormProvider>
  )
}

export default AgentWizard
